<template>
  <hb-modal
    v-model="show"
    size="large"
    :title="`${isEdit ? 'Edit' : 'Add'} Contact`"
    @close="onClose"
    :footerCancelOption="true"
  >
    <template v-slot:subheader>
      Only an email or phone number, along with a reason, is required to add a contact to the Do Not Rent list.
    </template>
    <template v-slot:content>
      <hb-form
        full
        label="Name"
      >
        <v-row no-gutters>
          <v-col
            cols="6"
            class="pr-2 mb-0"
          >
            <HbTextField
              v-validate="'max:45'"
              data-vv-as="First Name"
              v-model="contact.first"
              id="first"
              name="first"
              :error="!!errors.collect('first').length > 0"
              placeholder="First Name"
              @input="update"
              :disabled="(isEdit && !firstWasInitiallyEmpty && contact.first) ? true : false"
            />
          </v-col>

          <v-col cols="6" class="mb-0">
            <HbTextField
              v-validate="'max:45'"
              data-vv-as="Last Name"
              v-model="contact.last"
              id="last"
              name="last"
              :error="!!errors.collect('last').length > 0"
              placeholder="Last Name"
              @input="update"
              :disabled="(isEdit && !lastWasInitiallyEmpty && contact.last) ? true : false"
            />
          </v-col>
        </v-row>
      </hb-form>
      <hb-form
        full
        label="Email / Phone Number"
        required
      >
        <v-row class="mb-0">
          <v-col cols="6">
            <HbTextField
              :disabled="isEdit"
              :id="'email'"
              :name="'email'"
              v-validate="
                contact.Phone?.phone
                  ? 'max:256|email'
                  : 'required|max:256|email'
              "
              v-model.lazy="contact.email"
              label="Email"
              single-line
              data-vv-as="Email"
              @change="checkValidPhone(contact.Phone.code, contact.Phone.phone)"
              :error="errors.has('email')"
            ></HbTextField
          ></v-col>
        </v-row>
        <v-row class="mb-0">
          <v-col cols="6">
            <HbSelect
              :disabled="isEdit"
              :items="phone_types"
              item-text="label"
              item-value="value"
              v-model.lazy="contact.Phone.type"
              label="Type"
              single-line
              :id="'phone_type'"
              data-vv-as="Phone Type"
              :name="'phone_type'"
              v-validate="{
                required: isEdit ? false : contact.email && !contact.Phone.phone ? false : true,
              }"
              :error="errors.has('phone_type')"
            ></HbSelect>
          </v-col>
        </v-row>
        <v-row class="mb-0">
          <v-col cols="6">
            <v-row no-gutters>
              <v-col
                cols="4"
                class="pr-2 mb-0"
              >
                <HbAutocomplete
                  :disabled="isEdit"
                  :items="country_codes"
                  :clearable="false"
                  v-model="contact.Phone.code"
                  label="Code"
                  single-line
                  :id="'phone_code'"
                  data-vv-as="Phone Code"
                  :name="'phone_code'"
                  v-validate="{
                    required: isEdit ? false : contact.email && !contact.Phone.phone ? false : true,
                    max: 16,
                    numeric: isEdit ? false : true,
                  }"
                  :error="errors.has('phone_code')"
                  @change="
                    checkValidPhone(contact.Phone.code, contact.Phone.phone)
                  "
                  @focusout="
                    checkValidPhone(contact.Phone.code, contact.Phone.phone)
                  "
                  box
                >
                  <template v-slot:selection="data">
                    +{{ data.item }}
                  </template>
                  <template v-slot:item="data"> +{{ data.item }} </template>
                </HbAutocomplete>
              </v-col>
              <v-col cols="8" class="mb-0">
                <HbTextField
                  :disabled="isEdit"
                  v-model="contact.Phone.phone"
                  label="Number"
                  single-line
                  v-mask="'(###) ###-####'"
                  :id="'phone_number'"
                  data-vv-as="Phone Number"
                  :name="'phone_number'"
                  @change="
                    checkValidPhone(contact.Phone.code, contact.Phone.phone)
                  "
                  @focusout="
                    checkValidPhone(contact.Phone.code, contact.Phone.phone)
                  "
                  v-validate="{
                    required: isEdit ? false : contact.email ? false : true,
                    max: 16,
                  }"
                  :error="errors.has('phone_number') || isInvalidPhone"
                  box
                ></HbTextField>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </hb-form>
      <hb-form
        full
        label="Reason"
        required
      >
        <v-row no-gutters>
          <v-col cols="6">
            <HbTextarea
              v-model="contact.reason"
              placeholder="Reason For Do Not Rent(250 limit)"
              v-validate.disable="'required|max:250'"
              :maxlength="250" 
              data-vv-name="reason"
              data-vv-as="Reason"
              name="reason"
              id="reason"
              :rows="3"
              :error="errors.collect('reason').length > 0"
            />
          </v-col>
        </v-row>
      </hb-form>
    </template>

    <template v-slot:right-actions>
      <hb-btn
        color="primary"
        :loading="btnLoader"
        :disabled="btnLoader"
        @click="onSaveContact"
        >{{ `${isEdit ? "Update" : "Add"} Contact` }}</hb-btn
      >
    </template>
  </hb-modal>
</template>

<script>
import PHONE from "@/constants/phone.js";
import { parsePhoneNumber } from "libphonenumber-js";
import { validateEmailPhoneMixin } from "../../../mixins/validateEmailPhoneMixin.js";
import { notificationMixin } from "../../../mixins/notificationMixin.js";
import { mapGetters } from 'vuex';
export default {
  name: "AddContact",
  mixins: [notificationMixin, validateEmailPhoneMixin],

  data() {
    return {
      phone_types: PHONE.TYPES,
      country_codes: PHONE.COUNTRY_CODES,
      isInvalidPhone: false,
      contact: {
        first: "",
        last: "",
        email: "",
        Phone: {
          code: this.company_phone_code || "+1",
          phone: "",
          type: "",
        },
        reason: "",
      },
      firstWasInitiallyEmpty : false,
      lastWasInitiallyEmpty : false,
    };
  },
  beforeMount() {
    if (this.isEdit && this.editContact?.tenant_id) {
      const name = this.editContact?.tenant_name?.split(" ");
      const { tenant_email, tenant_phone, dnr_reason, tenant_phone_type, tenant_first, tenant_last } =
        this.editContact;
      const parsedPhone = this.formatPhone(tenant_phone);
      this.contact.first = tenant_first || '';
      this.contact.last = tenant_last || '';
      this.contact.email = tenant_email;
      this.contact.Phone.code = parsedPhone
        ? parsedPhone.countryCallingCode
        : this.company_phone_code;
      this.contact.Phone.phone = parsedPhone
        ? parsedPhone.nationalNumber
        : tenant_phone;
      this.contact.Phone.type = tenant_phone_type;
      this.contact.reason = dnr_reason;
      this.contact.id = this.editContact?.tenant_id;
    } else {
      this.contact = {
        first: "",
        last: "",
        email: "",
        Phone: {
          code: this.company_phone_code || "+1",
          phone: "",
          type: "",
        },
        description: "",
      };
    }
    this.firstWasInitiallyEmpty = !this.contact?.first;
    this.lastWasInitiallyEmpty = !this.contact?.last;
  },
  props: ["onClose", "show", "update", "isEdit", "editContact", "saveContact","btnLoader"],
  created(){
    console.log("this.company_phone_code:",this.company_phone_code);
  },
  computed: {
    ...mapGetters({
        company_phone_code: 'authenticationStore/getCompanyPhoneCode',
    }),
    uploadIsDisabled() {
      return this.contact ? false : true;
    },
  },
  methods: {
    formatPhone(value) {
      if (!value) return "";
      let parsedNumber = value;
      try {
        parsedNumber = parsePhoneNumber("+" + value);
      } catch (err) {}
      return parsedNumber;
    },
    checkValidPhone(code, number) {
      if (this.contact.email && !number) {
        this.isInvalidPhone = false;
        return;
      }
      let phone = "+" + code + number;
      try {
        let parsedPhoneNumber = parsePhoneNumber(phone);
        this.isInvalidPhone = !parsedPhoneNumber.isValid();
      } catch (err) {
        this.isInvalidPhone = true;
      }
    },
    resetPhoneValidation() {
      this.isInvalidPhone = false;
    },
    async onSaveContact() {
      let status = await this.$validator.validateAll();
      if (!status) return;
      else if (this.contact?.email && !this.contact.Phone.phone && this.isInvalidPhone){
        this.resetPhoneValidation();
      } else if (this.isInvalidPhone) {
        this.showMessageNotification({
          id: this.$options.name,
          type: "error",
          description: "Please Enter a Valid Phone Number",
        });
        return;
      }
      let contact = { ...this.contact };
      if(contact?.Phone?.phone && contact?.Phone?.code) {
        contact.Phone.phone = contact.Phone.phone.match(/\d+/g).join('');
        contact.Phone.phone =`${contact.Phone.code}${contact.Phone.phone}`
      }

      await this.$emit("saveContact", contact);
    },
  },
  watch: {
    // Watch the clear prop for changes
    clearData(newVal) {
      if (newVal) {
        this.contact = []; // Call clearData when clear prop changes
      }
    },
    editContact(newVal) {
      if (newVal) {
        if (this.isEdit && newVal?.name) {
          const name = newVal?.name?.split(" ");
          const { email, phone, note, type } = newVal;
          this.contact.first = name[0];
          this.contact.last = name[1];
          this.contact.email = email;
          this.contact.Phone = {};
          this.contact.Phone.code = this.company_phone_code || "+1";
          this.contact.Phone.phone = phone;
          this.contact.Phone.type = type;
          this.contact.reason = note;
        }
      }
    },
  },
};
</script>

<style scoped>
.label-div {
  background-color: #f9fafb;
  height: 48px;
  color: #101318;
}
</style>
